<script setup lang="ts">
  import { reactiveOmit } from '@vueuse/core';
  import type { DialogContentEmits, DialogContentProps } from 'reka-ui';
  import { DialogClose, DialogContent, DialogPortal, useForwardPropsEmits } from 'reka-ui';
  import type { HTMLAttributes } from 'vue';

  import { Button } from '@ui/components/button';
  import { useDismissHandler } from '@ui/utils/useDismissHandler';
  import type { DismissableProps } from '@ui/utils/useDismissHandler';
  import { cn } from '@ui/utils/utils';

  import DialogOverlay from './DialogOverlay.vue';

  const props = defineProps<
    DialogContentProps &
      DismissableProps & {
        class?: HTMLAttributes['class'];
        hideCloseBtn?: boolean;
        hideOverlay?: boolean;
        preventLauncher?: boolean;
      }
  >();

  const emits = defineEmits<DialogContentEmits & { close: [] }>();

  defineOptions({
    inheritAttrs: false,
  });

  const delegatedProps = reactiveOmit(props, 'class', 'disableDismissOn');

  const forwarded = useForwardPropsEmits(delegatedProps, emits);

  const { handleDismissInteraction } = useDismissHandler({
    disableDismissOn: props.disableDismissOn,
  });
</script>

<template>
  <DialogPortal>
    <DialogOverlay v-if="!hideOverlay" />

    <DialogContent
      data-slot="dialog-content"
      v-bind="{ ...$attrs, ...forwarded }"
      :class="
        cn(
          'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 rounded-sm border p-6 shadow-lg duration-200',
          props.class,
        )
      "
      @escape-key-down="handleDismissInteraction('esc', $event) && emits('close')"
      @interact-outside="handleDismissInteraction('interact-outside', $event) && emits('close')"
    >
      <slot />

      <!--      class="z-50 flex items-center absolute right-4 top-4 ring-offset-background transition-opacity focus:outline-hidden disabled:pointer-events-none" -->
      <DialogClose
        v-if="!hideCloseBtn"
        data-slot="dialog-close"
        class="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-2 right-2 rounded opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
      >
        <Button variant="ghost" size="icon-xs">
          <i class="fa far fa-close" />
        </Button>
        <span class="sr-only">Close</span>
      </DialogClose>
    </DialogContent>
  </DialogPortal>
</template>
